import axios, { type AxiosError, type  AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '../types';
import { msalInstance, acquireTokenForApi } from '../auth';

// -------------------------------------------------------------------------
// Key converters: camelCase ↔ snake_case
// -------------------------------------------------------------------------

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function convertKeysToSnake(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToSnake);
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[camelToSnake(key)] = convertKeysToSnake(value);
    }
    return result;
  }
  return obj;
}

function convertKeysToCamel(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToCamel);
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[snakeToCamel(key)] = convertKeysToCamel(value);
    }
    return result;
  }
  return obj;
}

// -------------------------------------------------------------------------
// Axios instance
// -------------------------------------------------------------------------

import { runtimeConfig } from '../config/runtime';

const BASE_URL = runtimeConfig.API_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// -------------------------------------------------------------------------
// Request interceptor — acquire MSAL token and attach as Bearer header.
// Falls back to localStorage for environments without Entra ID (dev/local).
// -------------------------------------------------------------------------

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Convert snake_case request body keys to camelCase for the Java backend
    if (config.data && typeof config.data === 'object') {
      config.data = convertKeysToCamel(config.data);
    }

    try {
      const token = await acquireTokenForApi(msalInstance);
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }
    } catch (_msalError) {
      // MSAL not initialized or no accounts — fall through to localStorage
    }

    // Fallback for dev/local environments without Entra ID
    const localToken = localStorage.getItem('auth_token');
    if (localToken && config.headers) {
      config.headers.Authorization = `Bearer ${localToken}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error)
);

// -------------------------------------------------------------------------
// Response interceptor — convert camelCase keys to snake_case, normalise errors
// -------------------------------------------------------------------------

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data && typeof response.data === 'object') {
      response.data = convertKeysToSnake(response.data);
    }
    return response;
  },
  (error: AxiosError<ApiError | { detail: string }>) => {
    if (error.response) {
      const status = error.response.status;

      // 401 — clear cached tokens and trigger re-authentication
      if (status === 401) {
        localStorage.removeItem('auth_token');
        const account = msalInstance.getActiveAccount();
        if (account) {
          // Clear MSAL token cache for the active account so next
          // request triggers a fresh token acquisition.
          msalInstance.logoutRedirect({ account });
        }
      }

      // 403
      if (status === 403) {
        console.warn('[api] Forbidden.');
      }

      // Surface a readable message
      const serverMessage =
        (error.response.data as ApiError)?.detail ?? error.message;
      return Promise.reject(new Error(serverMessage));
    }

    if (error.request) {
      return Promise.reject(
        new Error('No response received from the server. Please check your connection.')
      );
    }

    return Promise.reject(error);
  }
);

export default apiClient;
